import React, { FC, useEffect, useState } from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import debounce from 'lodash/debounce';
import { useStratsContext } from '@/finsight/application/Strats/StratsContext';
import PageHeaderWrapper from '@/finsight/ui/common/components/PageHeaderWrapper';
import Sidebar from './Sidebar';
import StratsGroups from '@/finsight/ui/components/strats/StratsTable/StratsGroups';
import Footnotes from './Footnotes';
import FiltersTable from './FiltersTable';
import DatatapeTable from './DatatapeTable';
import ApplyFilterPopover from './ApplyFilterPopover';
import MetaSchema from '@/finsight/ui/common/components/MetaSchema/MetaSchema';
import getStratsBreadcrumbs from '@/finsight/ui/components/strats/getStratsBreadcrumbs';
import { useBreadcrumbsContext } from '@/finsight/ui/common/components/header/Breadcrumbs/BreadcrumbsContext';
import ActionsBtn from './ActionsBtn';
import CheckUserPlanAccess from '@/finsight/ui/common/components/CheckUserPlanAccess';
import { UserPlanType } from '@/users/domain/UserPlan';
import { StratmakerNoAccessComponent } from '@/finsight/ui/components/strats/StratmakerNoAccess';
import { PAGE_TITLE } from '@/finsight/ui/components/strats/constants';
import MissingSomethingModalButton from '@/finsight/ui/common/components/MissingSomethingModalButton';

import styles from './strats.scss';
import spacesStyles from '@dealroadshow/uikit/core/styles/helpers/spaces.scss';

const Strats: FC = () => {
  const [isOpen, triggerOpen] = useState<boolean>(false);
  const { query: { fileId } } = useRouter();
  const { setBreadcrumbs } : any = useBreadcrumbsContext();
  const {
    updatePopoverPosition,
    popover: { currentTriggerComponentId },
    metadata: { filingTitle },
  } = useStratsContext();

  const updatePosition = () => {
    if (currentTriggerComponentId) {
      updatePopoverPosition(
        document.getElementById(currentTriggerComponentId),
        currentTriggerComponentId,
      );
    }
  };

  const debouncedUpdatePositionHandler = debounce(updatePosition, 500);

  useEffect(() => {
    window.addEventListener('resize', debouncedUpdatePositionHandler);
    // @ts-ignore
    setBreadcrumbs(getStratsBreadcrumbs(fileId));
    return () => {
      window.removeEventListener('resize', debouncedUpdatePositionHandler);
      setBreadcrumbs(null);
    };
  }, []);

  useEffect(() => {
    // @ts-ignore
    setBreadcrumbs(getStratsBreadcrumbs(fileId));
  }, [fileId]);

  const renderContent = () => (
    <>
      <div className={ styles.summaryRow }>
        <DatatapeTable />
        <FiltersTable />
      </div>
      <StratsGroups />
      <Footnotes />
      <Sidebar
        isOpen={ isOpen }
        triggerOpen={ triggerOpen }
      />
    </>
  );

  return (
    <>
      <MetaSchema />
      <ApplyFilterPopover />
      <CheckUserPlanAccess
        requiredUserPlan={ UserPlanType.PREMIUM }
        fallback={ (
          <PageHeaderWrapper
            title={ PAGE_TITLE }
            className={ styles.pageHeaderWrapper }
            requiredUserPlan={ UserPlanType.PREMIUM }
          />
        ) }
      >
        <PageHeaderWrapper
          title={ filingTitle }
          className={ styles.pageHeaderWrapper }
          requiredUserPlan={ UserPlanType.PREMIUM }
        >
          <ActionsBtn onClick={ () => triggerOpen(true) } />
        </PageHeaderWrapper>
      </CheckUserPlanAccess>
      <CheckUserPlanAccess
        requiredUserPlan={ UserPlanType.PREMIUM }
        fallback={ StratmakerNoAccessComponent }
      >
        { renderContent() }
      </CheckUserPlanAccess>
      <MissingSomethingModalButton className={ cn(spacesStyles.ptl, spacesStyles.pbxl) } />
    </>
  );
};

export default Strats;
